import React, { useEffect, useState } from 'react';
import type { ApoliceData } from '../../services/apoliceService';
import { getApoliceProdutoNome, getApoliceSeguradoraNome, getApoliceClienteNome } from '../../services/apoliceService';
import { repasseService } from '../../services/repasseService';
import { sinistroService } from '../../services/sinistroService';
import { documentoService } from '../../services/entitiesServices';
import SinistroFormModal from '../../components/modals/SinistroFormModal';
import RepasseFormModal from '../../components/modals/RepasseFormModal';
import AgendamentoFormModal from '../../components/modals/AgendamentoFormModal';
import { formatCpfCnpj, formatDate, formatCurrency } from '../../utils/formatters';
import { ConfirmDialog } from '../ui/ConfirmDialog';
import { useNotifier } from '../ui/Notifier';

interface ApoliceModalProps {
  apolice?: ApoliceData;
  isOpen: boolean;
  onClose: () => void;
  onEdit?: (apolice: ApoliceData) => void;
  onDelete?: (apolice: ApoliceData) => void;
  // when false, hide the Repasses tab and related actions
  showRepassesTab?: boolean;
}

export const ApoliceModal: React.FC<ApoliceModalProps> = ({ 
  apolice, 
  isOpen, 
  onClose, 
  onEdit, 
  onDelete,
  showRepassesTab = true
}) => {
  const a = apolice || ({} as ApoliceData);
  const [activeTab, setActiveTab] = useState<'dados' | 'parcelas' | 'repasses' | 'sinistros' | 'agendamentos'>('dados');
  const [repasses, setRepasses] = useState<any[]>([]);
  const [sinistros, setSinistros] = useState<any[]>([]);
  const [parcelas, setParcelas] = useState<any[]>([]);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [loadingRepasses, setLoadingRepasses] = useState(false);
  const [loadingSinistros, setLoadingSinistros] = useState(false);
  const [loadingParcelas, setLoadingParcelas] = useState(false);
  const [openSinistroModal, setOpenSinistroModal] = useState(false);
  const [openRepasseModal, setOpenRepasseModal] = useState(false);
  const [openAgendamentoModal, setOpenAgendamentoModal] = useState(false);
  const [initialDescricaoAgendamento, setInitialDescricaoAgendamento] = useState<string | undefined>(undefined);
  const notifier = useNotifier();

  useEffect(() => {
    if (!a?.id) return;

    // When opening modal, fetch documento with related parcelas,repasses,sinistros
    (async () => {
      try {
        setLoadingParcelas(true);
        setLoadingRepasses(true);
        setLoadingSinistros(true);
        const resp: any = await documentoService.getById(`${a.id}?include=parcelas,repasses,sinistros`);
        const doc = resp && (resp.data || resp) ? (resp.data || resp) : resp;

        // parcelas can be in doc.parcelas or doc.parcelas.data
        let parcelasList: any[] = [];
        if (doc) {
          if (Array.isArray(doc.parcelas)) parcelasList = doc.parcelas;
          else if (doc.parcelas && Array.isArray(doc.parcelas.data)) parcelasList = doc.parcelas.data;
        }
        setParcelas(parcelasList);

        // repasses
        let repList: any[] = [];
        if (doc) {
          if (Array.isArray(doc.repasses)) repList = doc.repasses;
          else if (doc.repasses && Array.isArray(doc.repasses.data)) repList = doc.repasses.data;
        }
        setRepasses(repList);

        // sinistros
        let sins: any[] = [];
        if (doc) {
          if (Array.isArray(doc.sinistros)) sins = doc.sinistros;
          else if (doc.sinistros && Array.isArray(doc.sinistros.data)) sins = doc.sinistros.data;
        }
        setSinistros(sins);
      } catch (err) {
        console.error('Erro ao carregar dados relacionados da apólice', err);
        notifier.show({ message: 'Erro ao carregar dados da apólice', severity: 'error' });
        setParcelas([]);
        setRepasses([]);
        setSinistros([]);
      } finally {
        setLoadingParcelas(false);
        setLoadingRepasses(false);
        setLoadingSinistros(false);
      }
    })();
  }, [a?.id]);

  if (!isOpen) return null;

  const handleEdit = () => {
    if (onEdit && apolice) {
      onEdit(apolice);
    }
  };

  const handleDelete = () => {
    setShowDeleteConfirm(true);
  };

  const confirmDelete = () => {
    if (onDelete && apolice) {
      onDelete(apolice);
      setShowDeleteConfirm(false);
    }
  };

  return (<>
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-1 overflow-auto">
      <div className="bg-white rounded max-w-xl w-full p-1 shadow-2xl max-h-[95vh] overflow-auto">
        <div className="flex justify-between items-center border-b pb-0.5 mb-1">
          <div>
            <h3 className="text-xs font-medium text-gray-900">Detalhes da Apólice</h3>
            <p className="text-xs text-gray-500 mt-0">
              {a.numeroDocumento || a.numeroApolice || a.codigoProposta || 'Sem identificação'}
            </p>
          </div>
          <button 
            onClick={onClose} 
            aria-label="fechar"
            className="p-0.5 hover:bg-gray-100 rounded transition-colors"
          >
            <svg className="w-2 h-2 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-1 mb-1">
          <div className="bg-gray-50 rounded p-1">
            <h4 className="text-xs font-medium mb-0.5 text-gray-700 flex items-center">
              <svg className="w-2 h-2 mr-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              Proposta
            </h4>
            <div className="space-y-0 text-xs">
              <div>
                <span className="block text-xs text-gray-600">Tipo:</span>
                <span className="text-xs font-medium ml-0.5">{a.tipoDocumento || '-'}</span>
              </div>
              <div>
                <span className="block text-xs text-gray-600">Número:</span>
                <span className="text-xs font-medium ml-0.5">{a.numeroDocumento || a.numeroApolice || '-'}</span>
              </div>
              <div>
                <span className="block text-xs text-gray-600">Proposta:</span>
                <span className="text-xs font-medium ml-0.5">{a.codigoProposta || '-'}</span>
              </div>
              {a.numeroEndosso && (
                <div>
                  <span className="block text-xs text-gray-600">Endosso:</span>
                  <span className="text-xs font-medium ml-0.5">{a.numeroEndosso}</span>
                </div>
              )}
            </div>
          </div>

          <div className="bg-blue-50 rounded p-1">
            <h4 className="text-xs font-medium mb-0.5 text-blue-700 flex items-center">
              <svg className="w-2 h-2 mr-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
              Cliente
            </h4>
            <div className="space-y-0 text-xs">
              <div>
                <span className="block text-xs text-blue-600">Nome:</span>
                <span className="text-xs font-medium ml-0.5 flex items-center gap-2">{a.cliente?.nome || '-'}
                  
                </span>
              </div>
              <div>
                <span className="block text-xs text-blue-600">CPF/CNPJ:</span>
                <span className="text-xs font-medium ml-0.5">{formatCpfCnpj(a.cliente?.cpfCnpj || a.cpfCnpjCliente || '')}</span>
              </div>
            </div>
          </div>

          <div className="bg-purple-50 rounded p-1">
            <h4 className="text-xs font-medium mb-0.5 text-purple-700 flex items-center">
              <svg className="w-2 h-2 mr-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
              </svg>
              Seguradora / Produto
            </h4>
            <div className="space-y-0 text-xs">
              <div>
                <span className="block text-xs text-purple-600">Seguradora:</span>
                <span className="text-xs font-medium ml-0.5">{a.seguradora?.nome || '-'}</span>
              </div>
              <div>
                <span className="block text-xs text-purple-600">Produto:</span>
                <span className="text-xs font-medium ml-0.5">{a.produto?.nome || '-'}</span>
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-1 mb-1">
          <div className="bg-green-50 rounded p-1">
            <h4 className="text-xs font-medium mb-0.5 text-green-700 flex items-center">
              <svg className="w-2 h-2 mr-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              Vigência
            </h4>
            <div className="space-y-0 text-xs">
              <div>
                <span className="block text-xs text-green-600">Início:</span>
                <span className="text-xs font-medium ml-0.5">{a.inicioVigencia ? formatDate(a.inicioVigencia) : '-'}</span>
              </div>
              <div>
                <span className="block text-xs text-green-600">Fim:</span>
                <span className="text-xs font-medium ml-0.5">{a.fimVigencia ? formatDate(a.fimVigencia) : '-'}</span>
              </div>
              {a.dataEmissao && (
                <div>
                  <span className="block text-xs text-green-600">Emissão:</span>
                  <span className="text-xs font-medium ml-0.5">{formatDate(a.dataEmissao)}</span>
                </div>
              )}
            </div>
          </div>
          
          <div className="bg-yellow-50 rounded p-1">
            <h4 className="text-xs font-medium mb-0.5 text-yellow-700 flex items-center">
              <svg className="w-2 h-2 mr-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              Valores
            </h4>
            <div className="space-y-0 text-xs">
              <div>
                <span className="block text-xs text-yellow-600">Prêmio Líquido:</span>
                <span className="text-xs font-medium text-green-600 ml-0.5">{formatCurrency(a.premioLiquido ?? null)}</span>
              </div>
              {/* Comissão intentionally hidden in view modal per request */}
              {a.quantidadeParcelas && (
                <div>
                  <span className="block text-xs text-yellow-600">Parcelas:</span>
                  <span className="text-xs font-medium ml-0.5">{a.quantidadeParcelas}</span>
                </div>
              )}
            </div>
          </div>
          
          <div className="bg-indigo-50 rounded p-1">
            <h4 className="text-xs font-medium mb-0.5 text-indigo-700 flex items-center">
              <svg className="w-2 h-2 mr-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              Status
            </h4>
            <div className="space-y-0 text-xs">
              <div>
                <span className="block text-xs text-indigo-600">Status:</span>
                <span className={`ml-0.5 px-1 py-0 rounded text-xs font-medium ${
                  a.status?.nome ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'
                }`}>
                  {a.status?.nome || 'Não definido'}
                </span>
              </div>
              <div>
                <span className="block text-xs text-indigo-600">Empresa:</span>
                <span className="text-xs font-medium ml-0.5">{a.empresa?.nome || '-'}</span>
              </div>
            </div>
          </div>
        </div>

        <div className="border-t pt-1">
          <div className="mb-2">
            <nav className="flex space-x-1 text-xs">
              <button className={`px-2 py-1 rounded ${activeTab === 'dados' ? 'bg-indigo-100' : 'bg-white'}`} onClick={() => setActiveTab('dados')}>Dados</button>
              <button className={`px-2 py-1 rounded ${activeTab === 'parcelas' ? 'bg-indigo-100' : 'bg-white'}`} onClick={() => setActiveTab('parcelas')}>Parcelas {parcelas.length > 0 ? `(${parcelas.length})` : ''}</button>
              {showRepassesTab && (
                <button className={`px-2 py-1 rounded ${activeTab === 'repasses' ? 'bg-indigo-100' : 'bg-white'}`} onClick={() => setActiveTab('repasses')}>Repasses {repasses.length > 0 ? `(${repasses.length})` : ''}</button>
              )}
              <button className={`px-2 py-1 rounded ${activeTab === 'sinistros' ? 'bg-indigo-100' : 'bg-white'}`} onClick={() => setActiveTab('sinistros')}>Sinistros {sinistros.length > 0 ? `(${sinistros.length})` : ''}</button>
              <button className={`px-2 py-1 rounded ${activeTab === 'agendamentos' ? 'bg-indigo-100' : 'bg-white'}`} onClick={() => setActiveTab('agendamentos')}>Agendamentos</button>
            </nav>
          </div>

          <div className="bg-white rounded p-2">
            {activeTab === 'dados' && (
              <div className="text-xs text-gray-700">(Exibição dos dados já presente acima)</div>
            )}

            {activeTab === 'parcelas' && (
              <div>
                <div className="flex justify-between items-center mb-2">
                  <h4 className="text-sm font-medium">Parcelas</h4>
                </div>
                {loadingParcelas ? <p>Carregando parcelas...</p> : (
                  <table className="min-w-full text-xs">
                    <thead>
                      <tr className="text-left"><th>#</th><th>Vencimento</th><th>Pagamento</th><th>Pago</th><th>Observações</th></tr>
                    </thead>
                    <tbody>
                      {parcelas.map(p => (
                        <tr key={p.id}><td>{p.numeroParcela}</td><td>{p.dataVencimento ? new Date(p.dataVencimento).toLocaleDateString() : '—'}</td><td>{p.dataPagamento ? new Date(p.dataPagamento).toLocaleDateString() : '—'}</td><td>{p.pago ? 'Sim' : 'Não'}</td><td>{p.observacoes || '—'}</td></tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            )}

            {showRepassesTab && activeTab === 'repasses' && (
              <div>
                <div className="flex justify-between items-center mb-2">
                  <h4 className="text-sm font-medium">Repasses</h4>
                  <div className="flex space-x-1">
                    <button className="px-2 py-1 text-xs bg-green-600 text-white rounded" onClick={() => setOpenRepasseModal(true)}>Novo Repasse</button>
                  </div>
                </div>
                {loadingRepasses ? <p>Carregando repasses...</p> : (
                  <table className="min-w-full text-xs">
                    <thead>
                      <tr className="text-left"><th>Cliente</th><th>Proposta</th><th>Data</th><th>Valor</th></tr>
                    </thead>
                    <tbody>
                      {repasses.map(r => (
                        <tr key={r.id}><td>{r.cliente}</td><td>{r.numeroDocumento}</td><td>{r.dataPagamento ? new Date(r.dataPagamento).toLocaleDateString() : '—'}</td><td>{r.repassePago}</td></tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            )}

            {activeTab === 'sinistros' && (
              <div>
                <div className="flex justify-between items-center mb-2">
                  <h4 className="text-sm font-medium">Sinistros</h4>
                  <div className="flex space-x-1">
                    <button className="px-2 py-1 text-xs bg-red-600 text-white rounded" onClick={() => setOpenSinistroModal(true)}>Novo Sinistro</button>
                  </div>
                </div>
                {loadingSinistros ? <p>Carregando sinistros...</p> : (
                  <table className="min-w-full text-xs">
                    <thead>
                      <tr className="text-left"><th>Número</th><th>Tipo</th><th>Data</th><th>Status</th></tr>
                    </thead>
                    <tbody>
                      {sinistros.map(s => (
                        <tr key={s.id}><td>{s.numero_sinistro}</td><td>{s.tipo}</td><td>{s.data ? new Date(s.data).toLocaleDateString() : '—'}</td><td>{s.concluido ? 'Concluído' : 'Aberto'}</td></tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            )}

            {activeTab === 'agendamentos' && (
              <div>
                <p className="text-xs">Agendamentos relacionados a este documento ainda não implementados na UI (placeholder).</p>
                <div className="mt-2">
                    <button className="px-2 py-1 text-xs bg-blue-600 text-white rounded" onClick={() => { setInitialDescricaoAgendamento(undefined); setOpenAgendamentoModal(true); }}>Novo Agendamento</button>
                  </div>
              </div>
            )}
          </div>
        </div>
        <div className="flex justify-between items-center mt-2">
          <div className="flex space-x-1">
            <button
              type="button"
              onClick={() => {
                const clienteNome = a.cliente?.nome || '';
                const produto = a.produto?.nome || a.produto || '';
                const seguradora = a.seguradora?.nome || a.seguradora || '';
                const apoliceNum = a.numeroDocumento || a.numeroApolice || a.codigoProposta || '';
                const endosso = a.numeroEndosso || a.endosso || '';
                const parts: string[] = [];
                if (clienteNome) parts.push(`Cliente: ${clienteNome}`);
                if (produto || seguradora) parts.push(`Produto - Seguradora: ${produto}${produto && seguradora ? ' - ' : ''}${seguradora}`);
                if (apoliceNum || endosso) parts.push(`Apólice - Endosso: ${apoliceNum}${apoliceNum && endosso ? ' - ' : ''}${endosso}`);
                const descricao = parts.join('\n');
                setInitialDescricaoAgendamento(descricao || undefined);
                setOpenAgendamentoModal(true);
              }}
              className="px-1 py-0 text-xs text-white bg-indigo-600 rounded hover:bg-indigo-700 transition-colors flex items-center"
            >
              Agendar
            </button>

            <button
              onClick={onClose}
              className="px-1 py-0 text-xs text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50 transition-colors"
            >
              Fechar
            </button>
            {onEdit && (
              <button
                onClick={handleEdit}
                className="px-1 py-0 text-xs text-white bg-blue-600 rounded hover:bg-blue-700 transition-colors flex items-center"
              >
                Editar
              </button>
            )}
            {onDelete && (
              <button
                onClick={handleDelete}
                className="px-1 py-0 text-xs text-white bg-red-600 rounded hover:bg-red-700 transition-colors flex items-center"
              >
                Excluir
              </button>
            )}
          </div>
        </div>
        </div>
      </div>
  
      {/* Modals de criação */}
      <SinistroFormModal documentoId={String(a.id ?? '')} isOpen={openSinistroModal} onClose={() => setOpenSinistroModal(false)} onSuccess={() => { setActiveTab('sinistros'); setTimeout(()=> setActiveTab('sinistros'), 50); }} />
      <RepasseFormModal documentoId={String(a.id ?? '')} isOpen={openRepasseModal} onClose={() => setOpenRepasseModal(false)} onSuccess={() => { setActiveTab('repasses'); setTimeout(()=> setActiveTab('repasses'), 50); }} />
      <AgendamentoFormModal 
        isOpen={openAgendamentoModal} 
        onClose={() => setOpenAgendamentoModal(false)} 
        onSuccess={() => { setActiveTab('agendamentos'); setTimeout(()=> setActiveTab('agendamentos'), 50); }} 
        documentoNumero={a.numeroDocumento || a.proposta || a.apolice}
        initialDescricaoAgendamento={initialDescricaoAgendamento}
      />

      {/* Dialog de confirmação de exclusão */}
      <ConfirmDialog
        isOpen={showDeleteConfirm}
        title="Excluir Documento"
        message="Tem certeza que deseja excluir este documento?"
        details={apolice ? [
          `Nº: ${apolice.numeroDocumento || apolice.numeroApolice || 'sem número'}`,
          `Cliente: ${getApoliceClienteNome(apolice)}`,
          `Produto: ${getApoliceProdutoNome(apolice)}`
        ] : []}
        onConfirm={confirmDelete}
        onCancel={() => setShowDeleteConfirm(false)}
        confirmText="Excluir"
        cancelText="Cancelar"
        confirmButtonColor="#DC2626"
      />
  </>);
};
